import {
  SCALE_GROUPED_FROM,
  scaleAnswerGroups,
  scalePoints,
  scaleValueLabel,
  declineStatements,
  type AnswerOption,
  type QuestionType,
} from '../../lib/votings';
import { __ } from '../../utils';
import { Box, Button, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Súhrn otázky ako **štruktúra, nie prúd viet**.
 *
 * Vzniklo to z návrhu `src/design_handoff_kontrola_pred_odoslanim` ku kontrole
 * pred odoslaním: každé tvrdenie škály sa dovtedy vypisovalo ako samostatná
 * veta („E0101 Názov projektu - nehodnotené") a pri otázke s dvadsiatimi
 * šiestimi tvrdeniami z toho boli tri obrazovky textu, v ktorých hlasujúci
 * svoju odpoveď nenašiel.
 *
 * Je to jeden súbor pre **dve obrazovky naraz**, a to je ten dôvod, prečo to
 * nie je napísané v tej kontrole: rovnaký zápis potrebuje aj zápis hlasovania
 * v administrácii ([[VotingLockedView]]). Tam sa vypisuje obsah otázky (čo sa
 * pýtala), tu odpoveď (čo hlasujúci odpovedal) - rám karty, meta riadok,
 * pilulky možností aj políčka hodnôt sú v oboch tie isté.
 *
 * Kto sem pridáva tretie použitie: telo karty je `children`, takže nový
 * prípad je nový `…Summary`, nie ďalší prepínač vo `QuestionSummaryCard`.
 */

/** Políčko s hodnotou škály. Najvyššia hodnota je plná, ostatné tiché. */
const ValueBox: React.FC<{
  children: React.ReactNode;
  /** Plný tmavý podklad - nesie ho najvyššia hodnota škály. */
  strong?: boolean;
  /** Žltá podoba pre nehodnotené tvrdenie. */
  missing?: boolean;
}> = ({ children, strong, missing }) => (
  <Box
    component='span'
    sx={({ palette, shape }) => ({
      flex: 'none',
      display: 'inline-grid',
      placeItems: 'center',
      minWidth: 24,
      height: 24,
      px: 0.75,
      borderRadius: `${shape.borderRadiusSm}px`,
      fontSize: 13,
      fontWeight: 700,
      ...(missing
        ? {
            backgroundColor: palette.yellow.surface,
            color: palette.yellow.surfaceText,
          }
        : strong
          ? {
              backgroundColor: palette.grey[800],
              color: palette.secondary.light,
            }
          : { backgroundColor: palette.grey[100], color: palette.grey[800] }),
    })}
  >
    {children}
  </Box>
);

/** Pilulka vybranej možnosti (viac možností) alebo možnosti v zadaní. */
const OptionPill: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    component='span'
    sx={({ palette, shape }) => ({
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: 26,
      px: 1.125,
      py: 0.25,
      borderRadius: `${shape.borderRadiusSm}px`,
      backgroundColor: palette.grey[100],
      color: palette.grey[800],
      fontSize: 14,
    })}
  >
    {children}
  </Box>
);

const StatementText: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Typography
    sx={{
      fontSize: 14,
      lineHeight: 1.45,
      color: 'grey.800',
      // Kódy projektov (`E0109`) stoja na začiatku tvrdenia, takže zalomenie
      // nesmie nechať na riadku samotné slovo - `pretty` to rieši v prehliadači.
      textWrap: 'pretty',
    }}
  >
    {children}
  </Typography>
);

/**
 * Riadok „ostatných N sa nevypisuje" s tlačidlom na rozbalenie.
 *
 * Kľúčová vec návrhu: dlhý zoznam sa **neskrýva za scroll, ale za vetu**.
 * Rozbalenie je stav karty, nie navigácia - preto je to obyčajný `useState`
 * v komponente, ktorý riadok používa.
 */
const MoreLine: React.FC<{
  text: string;
  expanded: boolean;
  onToggle: () => void;
}> = ({ text, expanded, onToggle }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    gap={{ xs: 0, sm: 1.25 }}
    sx={({ palette }) => ({
      pt: 1,
      borderTop: `1px solid ${palette.grey[100]}`,
    })}
  >
    <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
      {text}
    </Typography>
    <Button
      variant='text'
      size='small'
      onClick={onToggle}
      sx={{ flex: 'none', color: 'blue.dark', fontWeight: 700 }}
    >
      {expanded ? __('Skryť') : __('Zobraziť viac')}
    </Button>
  </Stack>
);

/**
 * Karta jednej otázky v súhrne: značka, meta riadok, otázka, telo a akcia.
 *
 * Na mobile stojí akcia **v pravom hornom rohu karty**, nie pri odpovedi -
 * v jednom riadku s tromi slovami odpovede by z nej urobila stĺpec širokým
 * dvadsať pixelov.
 */
export const QuestionSummaryCard: React.FC<{
  /** Kruh alebo dlaždica vľavo - stav odpovede, alebo typ otázky. */
  mark: React.ReactNode;
  meta: string;
  /** Meta riadok zožltne, keď v odpovedi niečo chýba. */
  metaTone?: 'neutral' | 'warning';
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ mark, meta, metaTone = 'neutral', title, action, children }) => (
  <Stack
    direction='row'
    alignItems='flex-start'
    gap={{ xs: 1.25, md: 1.75 }}
    sx={({ palette, shape }) => ({
      backgroundColor: palette.background.paper,
      border: `1px solid ${
        metaTone === 'warning' ? palette.yellow.main : palette.divider
      }`,
      borderRadius: `${shape.borderRadius}px`,
      p: { xs: 1.75, md: 2.5 },
    })}
  >
    <Box sx={{ flex: 'none', mt: 0.25 }}>{mark}</Box>

    <Stack gap={1.25} sx={{ flex: 1, minWidth: 0 }}>
      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='space-between'
        gap={1.5}
      >
        <Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant='body2'
            sx={{
              fontWeight: 500,
              color: metaTone === 'warning' ? 'yellow.surfaceText' : 'grey.500',
            }}
          >
            {meta}
          </Typography>
          <Typography
            component='b'
            sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.35 }}
          >
            {title}
          </Typography>
        </Stack>

        {/* Na mobile je akcia tu, na desktope v treťom stĺpci karty. */}
        {action && (
          <Box sx={{ flex: 'none', display: { xs: 'block', md: 'none' } }}>
            {action}
          </Box>
        )}
      </Stack>

      {children}
    </Stack>

    {action && (
      <Box sx={{ flex: 'none', display: { xs: 'none', md: 'block' } }}>
        {action}
      </Box>
    )}
  </Stack>
);

type AnswerShape = {
  question_id: number;
  type: QuestionType;
  min_options: number;
  max_options: number;
  scale_min?: number;
  scale_max?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  options: {
    option_id: number;
    type: 'choice' | 'free';
    title: string;
    required?: boolean;
  }[];
};

/**
 * Odpoveď na otázku ako štruktúra podľa typu - telo karty v kontrole pred
 * odoslaním.
 *
 * Krátka škála je mriežka „hodnota - tvrdenie", dlhá (od
 * `SCALE_GROUPED_FROM` tvrdení) sa zoskupuje podľa hodnotenia zhora dole
 * a **nehodnotené tvrdenia sa nevypisujú** - zhrnú sa do jednej vety, ktorá
 * sa dá rozbaliť. Bez toho mala jedna otázka s dvadsiatimi šiestimi
 * tvrdeniami dlhšiu kartu než celý zvyšok kontroly.
 */
export const AnswerSummary: React.FC<{
  question: AnswerShape;
  answers: AnswerOption[] | undefined;
}> = ({ question, answers }) => {
  const [expanded, setExpanded] = React.useState(false);
  const value = answers ?? [];

  if (question.type === 'free_text') {
    const text = value.find((answer) => answer.text?.trim())?.text?.trim();
    return text ? (
      <Typography sx={{ fontSize: 15, lineHeight: 1.5 }}>{text}</Typography>
    ) : (
      <Typography sx={{ fontSize: 15 }} color='text.secondary'>
        {__('nevyplnené')}
      </Typography>
    );
  }

  if (question.type === 'single_choice' || question.type === 'multi_choice') {
    const chosen = value.map((answer) => {
      const option = question.options.find(
        (candidate) => candidate.option_id === answer.id,
      );
      // Vlastná odpoveď („Iné") sa vypisuje textom, ktorý hlasujúci napísal -
      // názov možnosti je len jej rám.
      return option?.type === 'free'
        ? (answer.text?.trim() ?? '')
        : (option?.title ?? '');
    });

    if (!chosen.length) {
      return (
        <Typography sx={{ fontSize: 15 }} color='text.secondary'>
          {__('Bez odpovede')}
        </Typography>
      );
    }

    // Jedna možnosť je jeden riadok, viac možností sú pilulky vedľa seba:
    // pilulka pri jedinej odpovedi vyzerá ako filter, ktorý sa dá vypnúť.
    return question.type === 'single_choice' ? (
      <Typography sx={{ fontSize: 15, color: 'grey.800' }}>
        {chosen[0]}
      </Typography>
    ) : (
      <Stack direction='row' flexWrap='wrap' gap={0.75}>
        {chosen.map((title, index) => (
          <OptionPill key={`${index}-${title}`}>{title}</OptionPill>
        ))}
      </Stack>
    );
  }

  const { groups, unrated, highest } = scaleAnswerGroups(question, answers);

  if (question.options.length < SCALE_GROUPED_FROM) {
    const rated = new Map(
      value
        .filter((answer) => typeof answer.value === 'number')
        .map((answer) => [answer.id, answer.value as number]),
    );

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'center',
          gap: '4px 10px',
        }}
      >
        {question.options.map((option) => {
          const rating = rated.get(option.option_id);
          const isMissing = rating === undefined;

          return (
            <React.Fragment key={option.option_id}>
              <ValueBox missing={isMissing}>
                {isMissing ? '-' : rating}
              </ValueBox>
              <Typography
                sx={{
                  fontSize: 14,
                  color: isMissing ? 'yellow.surfaceText' : 'grey.800',
                }}
              >
                {isMissing
                  ? `${option.title} · ${__('nehodnotené')}`
                  : option.title}
              </Typography>
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  return (
    <Stack gap={1}>
      {groups.map((group, index) => (
        <React.Fragment key={group.value}>
          {index > 0 && (
            <Box
              sx={({ palette }) => ({
                height: '1px',
                backgroundColor: palette.grey[100],
                // Na mobile sú skupiny bez liniek a bez nadpisov - je tam
                // jeden stĺpec a linka z neho robí tabuľku.
                display: { xs: 'none', md: 'block' },
              })}
            />
          )}
          <Stack direction='row' gap={1.5}>
            <ValueBox strong={group.value === highest}>{group.value}</ValueBox>
            <Stack gap={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  color: 'grey.500',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {`${scaleValueLabel(question, group.value)} · ${group.statements.length} ${declineStatements(group.statements.length)}`}
              </Typography>
              {group.statements.map((statement) => (
                <StatementText key={statement}>{statement}</StatementText>
              ))}
            </Stack>
          </Stack>
        </React.Fragment>
      ))}

      {unrated.length > 0 && (
        <>
          <MoreLine
            text={
              unrated.every((statement) => !statement.required)
                ? `${__('Ostatných')} ${unrated.length} ${declineStatements(unrated.length)} ${__('ste nehodnotili - hodnotenie je nepovinné.')}`
                : `${__('Nehodnotených tvrdení:')} ${unrated.length}, ${__('z toho povinných:')} ${unrated.filter((statement) => statement.required).length}.`
            }
            expanded={expanded}
            onToggle={() => setExpanded((current) => !current)}
          />

          {expanded && (
            <Stack gap={0.25}>
              {unrated.map((statement) => (
                <Typography
                  key={statement.title}
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.45,
                    textWrap: 'pretty',
                    color: statement.required
                      ? 'yellow.surfaceText'
                      : 'text.secondary',
                  }}
                >
                  {statement.required
                    ? `${statement.title} · ${__('povinné')}`
                    : statement.title}
                </Typography>
              ))}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

type ContentShape = {
  type: QuestionType;
  scale_min?: number;
  scale_max?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  options: {
    option_id: number;
    type: 'choice' | 'free';
    title: string;
    description?: string;
  }[];
};

/**
 * Koľko možností či tvrdení sa v zápise hlasovania vypíše, kým sa zoznam
 * nerozbalí celý. Osem je riadok pilulek a pri tvrdeniach osem riadkov -
 * ďalej už karta prerastie panel výsledkov vedľa seba.
 */
const CONTENT_PREVIEW = 8;

/**
 * Obsah otázky - telo karty v zápise hlasovania pre úradníka.
 *
 * Je to ten istý zápis ako pri odpovedi, len bez odpovede: možnosti sú
 * pilulky, škála má rad políčok s hodnotami (a slovami na krajoch, ak ich
 * úradník zadal) a pod ním tvrdenia. Nahradilo to riadok
 * „Škála 1 - 5 · 26 tvrdení · Prvé, Druhé, Tretie a ďalších 23", z ktorého
 * sa nedalo prečítať ani jedno celé tvrdenie.
 */
export const QuestionContentSummary: React.FC<{ question: ContentShape }> = ({
  question,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (question.type === 'free_text') {
    return (
      <Typography sx={{ fontSize: 15 }} color='text.secondary'>
        {__('Občan odpovedá vlastnými slovami.')}
      </Typography>
    );
  }

  const items = question.options.filter((option) => option.type === 'choice');
  const shown = expanded ? items : items.slice(0, CONTENT_PREVIEW);
  const rest = items.length - shown.length;

  if (question.type === 'scale') {
    const points = scalePoints(question);

    return (
      <Stack gap={1.25}>
        {/* Rozsah škály: rad políčok s hodnotami a, ak ich úradník zadal, slová
            na krajoch. Toto je jediné miesto, kde sa políčka hodnôt kreslia -
            v zápise otázky nemá žiadne tvrdenie hodnotenie, takže sa **podľa
            hodnotenia nezoskupuje** (to robí až kontrola pred odoslaním nad
            vyplneným lístkom). */}
        <Stack direction='row' alignItems='center' flexWrap='wrap' gap={0.75}>
          {points.map((point) => (
            <ValueBox key={point} strong={point === points[points.length - 1]}>
              {point}
            </ValueBox>
          ))}
          {(question.scale_min_label || question.scale_max_label) && (
            <Typography variant='body2' color='text.secondary'>
              {`${scaleValueLabel(question, points[0])} - ${scaleValueLabel(question, points[points.length - 1])}`}
            </Typography>
          )}
        </Stack>

        {/* Tvrdenia ako číslovaný zoznam s hairline linkou medzi nimi, nie
            prúd riadkov nalepených na seba - poradie, názov a (ak je) popis
            zámeru projektu. */}
        <Stack gap={0}>
          {shown.map((option, index) => (
            <Stack
              key={option.option_id}
              direction='row'
              gap={1.25}
              sx={({ palette }) => ({
                py: 0.875,
                borderTop:
                  index > 0 ? `1px solid ${palette.grey[100]}` : 'none',
              })}
            >
              <Typography
                sx={{
                  flex: 'none',
                  width: 20,
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1.55,
                  color: 'grey.400',
                }}
              >
                {index + 1}
              </Typography>
              <Stack gap={0.25} sx={{ minWidth: 0 }}>
                <StatementText>{option.title}</StatementText>
                {option.description && (
                  <Typography variant='body2' color='text.secondary'>
                    {option.description}
                  </Typography>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>

        {items.length > CONTENT_PREVIEW && (
          <MoreLine
            text={
              expanded
                ? `${__('Vypísaných všetkých')} ${items.length} ${declineStatements(items.length)}.`
                : `${__('Ďalších')} ${rest} ${declineStatements(rest)} ${__('sa nevypisuje.')}`
            }
            expanded={expanded}
            onToggle={() => setExpanded((current) => !current)}
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={1.25}>
      <Stack direction='row' flexWrap='wrap' gap={0.75}>
        {shown.map((option) => (
          <OptionPill key={option.option_id}>{option.title}</OptionPill>
        ))}
      </Stack>

      {/* Možnosť „Iné" nie je v zozname, je to pravidlo otázky - preto veta,
          nie pilulka bez textu. */}
      {question.options.some((option) => option.type === 'free') && (
        <Typography variant='body2' color='text.secondary'>
          {__('Otázka má aj vlastnú odpoveď („Iné").')}
        </Typography>
      )}

      {items.length > CONTENT_PREVIEW && (
        <MoreLine
          text={
            expanded
              ? `${__('Vypísaných všetkých')} ${items.length} ${__('možností.')}`
              : `${__('Ďalších')} ${rest} ${__('možností sa nevypisuje.')}`
          }
          expanded={expanded}
          onToggle={() => setExpanded((current) => !current)}
        />
      )}
    </Stack>
  );
};
